import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Panel } from '@/components/dashboard/Panel';
import { formatAgo } from '@/lib/ago';
import { copyText } from '@/lib/copyText';
import type { Container } from '@/types/container';

const linkClass = 'rounded underline underline-offset-4 outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

function When({ iso }: { iso: string | undefined }) {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return <span className="text-muted-foreground">Unknown</span>;
  return (
    <span>
      <time dateTime={new Date(t).toISOString()}>{formatAgo(t)}</time>
      <span className="text-muted-foreground"> ({new Date(t).toLocaleString()})</span>
    </span>
  );
}

const none = <span className="text-muted-foreground">None</span>;

export function ContainerOverview({ container }: { container: Container }) {
  const ports = container.ports ?? [];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Details" loading={false} error={null} hasData>
        <dl className="space-y-2">
          <Row label="Image">
            <span className="font-mono text-xs">{container.image}</span>
          </Row>
          <Row label="Instance">{container.instance?.name ?? none}</Row>
          <Row label="Endpoint">
            {container.endpoint ? (
              <Link to={`/resources/endpoints/${container.endpoint.id}`} className={linkClass}>
                {container.endpoint.name}
              </Link>
            ) : (
              none
            )}
          </Row>
          <Row label="Stack">{container.stack?.name ?? none}</Row>
          <Row label="Portainer ID">
            <span className="inline-flex items-center gap-1">
              <span className="font-mono text-xs">{container.portainer_id}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Copy Portainer ID"
                onClick={() => void copyText(container.portainer_id)}
              >
                <Copy className="size-4" aria-hidden />
              </Button>
            </span>
          </Row>
          <Row label="Created">
            <When iso={container.created_at} />
          </Row>
          <Row label="Updated">
            <When iso={container.updated_at} />
          </Row>
        </dl>
      </Panel>
      <Panel title="Ports" loading={false} error={null} hasData>
        {ports.length === 0 ? (
          <p className="text-sm text-muted-foreground">No published ports.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mapping</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">Copy</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ports.map((p) => {
                const mapping = `${p.external}:${p.internal}/${p.protocol}`;
                return (
                  <TableRow key={mapping}>
                    <TableCell className="font-mono text-xs">{mapping}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Copy ${mapping}`}
                        onClick={() => void copyText(mapping)}
                      >
                        <Copy className="size-4" aria-hidden />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Panel>
    </div>
  );
}
